import React from 'react';
import { Typography, Button, Paper, Box } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { debugService } from '../services/debugService';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallbackMessage?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

function isChunkError(error: Error | null): boolean {
  if (!error) return false;
  const msg = error.message || '';
  return (
    msg.includes('Loading chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('ChunkLoadError') ||
    error.name === 'ChunkLoadError'
  );
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    debugService.logReactError(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const chunkError = isChunkError(this.state.error);

      return (
        <Paper sx={{ p: 3, m: 2, textAlign: 'center', borderRadius: '6px', border: '1px solid', borderColor: chunkError ? 'warning.main' : 'error.main' }}>
          <Typography variant="h6" color={chunkError ? 'warning.main' : 'error'} gutterBottom>
            {chunkError ? 'New Version Available' : 'Something went wrong'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {chunkError
              ? 'The app has been updated. Please reload to get the latest version.'
              : (this.props.fallbackMessage || 'This section encountered an error. Try refreshing the page.')}
          </Typography>
          {!chunkError && this.state.error && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 2, fontFamily: 'monospace' }}>
              {this.state.error.message}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            {chunkError ? (
              <Button
                variant="contained"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => window.location.reload()}
              >
                Reload App
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => this.setState({ hasError: false, error: null })}
                >
                  Try Again
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => window.location.reload()}
                >
                  Reload Page
                </Button>
              </>
            )}
          </Box>
        </Paper>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
